import {
  движокПошёл,
  движокПриехал,
  завестиСчётчикЗагрузки,
  спроситьРазмеры,
} from './download-meter.js';
import { bindLoginForm, loginByStoredToken, type Вошедший } from './net/login.js';
import { session } from './session.js';
import { t } from './i18n.js';
import * as loader from './ui/loader.js';
import './ui/language.js';

/**
 * Первый экран игры: вход, рыболовная база — и только потом сама рыбалка.
 *
 * ⚠️ ЗДЕСЬ НЕТ НИ ОДНОЙ СТРОЧКИ ДВИЖКА, И ЭТО ГЛАВНОЕ. Замерено на сервере
 * разработки: страница тянула 14 МБ в 250 запросах, из них 8.5 МБ — один файл
 * Babylon, ещё 5 МБ — код игры (снасть, шейдер воды, физика игрока). Всё это
 * скачивалось, пока человек смотрел на поле пароля, и до готовности проходило
 * 38.9 секунды. После разделения — 610 КБ в 16 запросах за 224 мс.
 *
 * Ни входу, ни базе движок не нужен: форма, словарь, журнал. Babylon приходит
 * динамическим ввозом ровно в тот миг, когда выбран водоём и нажат штамп «На
 * рыбалку», — и ни секундой раньше.
 *
 * ⚠️ ПОРЯДОК ТУТ НЕ ПРОСТО «БЫСТРЕЕ». Водоёмы размечает админ по настоящей
 * карте; выдуманных по зерну больше нет. Нет водоёма — ловить негде, и
 * поднимать ради этого движок незачем.
 */

/**
 * Спрятать или показать форму входа.
 *
 * ⚠️ ПРЯЧЕМ ДО ПЕРВОЙ ОТРИСОВКИ, А НЕ ПОСЛЕ ОТВЕТА СЕРВЕРА. Ключ лежит в
 * localStorage и читается мгновенно; проверка же его на сервере занимает
 * десятки миллисекунд. Спроси мы сначала сервер — человек с сохранённым
 * входом успел бы увидеть вспышку формы, которая ему не нужна.
 */
function форма(видна: boolean): void {
  /*
   * ⚠️ КЛАСС СНИМАЕМ, КОГДА ФОРМА И ПРАВДА НУЖНА. Разметка прячет её заранее,
   * если в localStorage лежит ключ (встроенный сценарий в <head> — там же и
   * разбор). Ключ мог протухнуть; тогда форму надо показать, а спрятавший её
   * класс — убрать, иначе она осталась бы невидимой навсегда.
   */
  document.documentElement.classList.toggle('if-with-key', !видна);
  const узел = document.getElementById('auth-container');
  if (узел !== null) узел.style.display = видна ? 'flex' : 'none';
}

/**
 * Поднять движок и передать ему выбранный водоём.
 *
 * ⚠️ ВОДОЁМ ПЕРЕДАЁТСЯ ЧЕРЕЗ session, А НЕ ДОВОДОМ. Игра — это модуль,
 * который сам себя запускает при ввозе; принять довод ему некуда. session.ts
 * свободен от движка и виден обеим сторонам, поэтому и служит передаточным
 * звеном: боот кладёт выбор, игра его забирает.
 */
/**
 * Сколько раз ещё стоит перечитать страницу, прежде чем сдаться.
 *
 * ⚠️ СЧЁТЧИК ЖИВЁТ В ПАМЯТИ ВКЛАДКИ, А НЕ В АДРЕСЕ, И ЭТО ЛЕЧЕНИЕ
 * ЗАМЕРЕННОЙ БЕДЫ. Здесь стояла метка `?if-fresh=...` в ссылке. Она делала
 * своё дело ровно один раз, а потом ЗАЛИПАЛА: адрес с меткой оставался во
 * вкладке навсегда, и всякий следующий заход шёл сразу к жалобе, не сделав
 * ни одной попытки. На iPhone это и вышло — метка висела в адресной строке
 * сутки, и лечение, задуманное как первая помощь, оказалось выключено.
 *
 * ⚠️ ПАМЯТЬ ВКЛАДКИ ОЧИЩАЕТСЯ САМА, когда вкладку закрывают. Значит новый
 * заход всегда получает свои три попытки, а адрес остаётся чистым.
 *
 * ⚠️ ТРИ ПОПЫТКИ, А НЕ ОДНА. Отказ ввоза на телефоне бывает и разовым —
 * оборвалась связь на самом большом файле. Одна попытка объявляет игру
 * сломанной там, где хватило бы второй.
 */
const ПОПЫТОК = 3;

function ещёПробуем(): boolean {
  try {
    const было = Number(window.sessionStorage.getItem('if-попытка') ?? '0');
    if (было + 1 >= ПОПЫТОК) return false;
    window.sessionStorage.setItem('if-попытка', String(было + 1));
    return true;
  } catch {
    // Нет памяти вкладки (тайный просмотр у старых браузеров) — не повторяем
    // вовсе: без счётчика повтор превратился бы в бесконечный круг.
    return false;
  }
}

/**
 * Игра пошла: забыть попытки и убрать из адреса метку прошлых лечений.
 *
 * ⚠️ АДРЕС ЧИСТИМ ИМЕННО ЗДЕСЬ, ПОСЛЕ УСПЕХА. Метки `?if-fresh` уже
 * разъехались по вкладкам и закладкам игроков; сама она не исчезнет никогда,
 * а мешает — с ней ссылка выглядит сломанной и переезжает в закладки.
 */
function всёХорошо(): void {
  try {
    window.sessionStorage.removeItem('if-попытка');
  } catch {
    /* памяти вкладки нет — и не надо */
  }
  const адрес = new URL(window.location.href);
  if (адрес.searchParams.has('if-fresh')) {
    адрес.searchParams.delete('if-fresh');
    window.history.replaceState(null, '', адрес.toString());
  }
}

async function поднятьИгру(файл: string | null): Promise<void> {
  session.pendingWorldFile = файл;

  loader.показать('рыбалка');

  /*
   * ⚠️ СЧЁТЧИК МЕГАБАЙТ ЗАВОДИТСЯ ЗДЕСЬ, ДО ВВОЗА ДВИЖКА, И ЭТО ГЛАВНОЕ В НЁМ.
   * Он спрашивает у сервера размеры файлов, а браузер держит к одному узлу
   * шесть соединений: начни он позже — эти вопросы встали бы в очередь за
   * восемью мегабайтами движка и тридцатью — снасти, и ответ пришёл бы
   * последним. Ровно поэтому строка с мегабайтами раньше выскакивала в самом
   * конце и сразу со «всё готово».
   */
  завестиСчётчикЗагрузки();

  /*
   * ⚠️ ПРО ДВИЖОК СТРОКА ГОВОРИТ ОТСЮДА, И СКАЗАТЬ БОЛЬШЕ НЕОТКУДА. Модульный
   * ввоз не сообщает о себе НИЧЕГО, пока не кончится: ни байта, ни события. А
   * это самый длинный кусок ожидания за весь вход — семь мегабайт одним
   * файлом. Замерено на собранной игре: сорок четыре секунды, за которые
   * счётчик не получил ни одного повода обновить строку, и та стояла на
   * последнем, что успела показать до ввоза.
   *
   * Строка, честно стоящая на «движок игры», отвечает на вопрос «почему
   * задумалось» — а строка, стоящая на «снасть · 0.2 МБ», на него врёт.
   */
  движокПошёл();

  /*
   * ⚠️ ОТКАЗ ЭТОГО ВВОЗА ОБЯЗАН БЫТЬ ВИДЕН, И ЭТО ЗАМЕРЕННАЯ БЕДА, А НЕ
   * ПРЕДОСТОРОЖНОСТЬ. Без ловли отказ уходил в необработанное обещание:
   * полоса замирала на двадцати двух процентах — ровно середина этапа
   * движка — и стояла так до сторожа, то есть три минуты. Человек читает
   * это как «игра зависла» и винит свой телефон.
   *
   * ⚠️ ЧАЩЕ ВСЕГО ВИНОВАТ КЕШ РАЗМЕТКИ, А НЕ СЕТЬ. Браузер держит старый
   * index.html, тот просит кусок по старому имени, а на сервере лежат уже
   * новые — и приходит 404 на самый большой файл игры. Лечится это
   * перезагрузкой, но узнать об этом можно только если сказать.
   */
  try {
    // Динамический ввоз: до этой строки Babylon не скачивается вовсе.
    await import('./main.js');
  } catch (беда) {
    /*
     * ⚠️ СПЕРВА ПРОБУЕМ ПЕРЕЧИТАТЬ СТРАНИЦУ, И ТОЛЬКО ПОТОМ ЖАЛУЕМСЯ. Девять
     * раз из десяти причина одна: разметка старая — из кеша или из вкладки,
     * открытой неделю назад, — и она просит кусок по имени, которого на
     * сервере уже нет. Свежая разметка лечит это сама, и просить игрока
     * чистить кеш не за что.
     *
     * ⚠️ АДРЕС С МЕТКОЙ, А НЕ ПРОСТАЯ ПЕРЕЗАГРУЗКА: ту же ссылку браузер
     * вправе отдать из кеша, и всё повторилось бы по кругу. И ровно один раз:
     * если после обновления кусок всё равно не пришёл, дело не в кеше, а в
     * неполной выкладке — второй круг ничего не изменит.
     */
    console.error('движок не загрузился', беда);
    if (ещёПробуем()) {
      // Даём сети выдохнуть: сразу за отказом повтор чаще всего падает так же.
      window.setTimeout(() => window.location.reload(), 900);
      return;
    }
    /*
     * ⚠️ ТЕКСТ ОШИБКИ ПОКАЗЫВАЕМ ТУТ ЖЕ, И БЕЗ НЕГО ЖАЛОБА БЕСПОЛЕЗНА. Ловим
     * беду мы давно, а показывали одно ласковое «обновите страницу» — с ним
     * человек шлёт снимок экрана, на котором ровно ноль сведений. Причина же
     * лежит в пойманном исключении, и на телефоне это единственный способ её
     * прочитать: ни консоли, ни отладчика там нет.
     */
    const словами = беда instanceof Error ? (беда.stack ?? беда.message) : String(беда);
    loader.сбой(`${t('load.engine_failed')}\n${словами.slice(0, 400)}`);
    return;
  }
  движокПриехал();
  всёХорошо();
  /*
   * ⚠️ ТОЛЬКО ТЕПЕРЬ СПРАШИВАЕМ ВЕС ФАЙЛОВ МИРА. Пока ехал движок, сеть
   * обязана была принадлежать ему одному: два десятка попутных запросов
   * занимали все соединения и на iPhone отбирали у него загрузку целиком.
   */
  void спроситьРазмеры();

  /*
   * ⚠️ ЗДЕСЬ БОЛЬШЕ НЕТ НИКАКОГО «ПОЛА», И ЭТО ГЛАВНАЯ ПЕРЕМЕНА В ЭТОМ ФАЙЛЕ.
   *
   * Стояло `шаг('движок', 0.5)`: мол, движок скачался и запустился, полэтапа
   * он заслужил. Замысел был подпереть полосу на случай молчащего счётчика
   * байтов, а вышла мёртвая зона длиной в половину всей загрузки. Пол — это
   * НИЖНЯЯ граница доли, и пока настоящие байты её не перебьют, полоса стоит.
   * Замерено стендом: 33 % держались от нуля до 50 % скачанного объёма — то
   * есть ровно на самом длинном участке ожидания, ради которого всё и
   * затевалось.
   *
   * Пустоту, которую пол затыкал, теперь закрывает ползок показа (см.
   * ui/loader.ts): полоса и в тишине заметно движется, но упирается в потолок
   * от НЕЗАВЕРШЁННОГО этапа и правду не обгоняет. Подпирать долю враньём
   * больше незачем.
   */
}

/**
 * Открыть базу: собрать данные, показать журнал, дождаться решения.
 *
 * ⚠️ ЛОАДЕР ЗДЕСЬ ЛЁГКИЙ, В ДВА ЭТАПА. База открывается за доли секунды —
 * четыре запроса к серверу и разметка. Показывать ей ту же полоску, что
 * рассчитана на восемь мегабайт движка, значит мигнуть тремя процентами и
 * тут же исчезнуть.
 */
async function база(кто: Вошедший): Promise<void> {
  форма(false);
  loader.показать('база');
  loader.шаг('ключ', 1);

  /**
   * ⚠️ ЖУРНАЛ ВВОЗИТСЯ ДИНАМИЧЕСКИ, И ЭТО НЕ ПРИДИРКА. Обычным ввозом наверху
   * он тянул на экран входа 110 КБ: сам журнал со стилями, а следом — весь
   * справочник рыбы на 52 КБ, и тот единственно ради формулы уровня, по
   * которой рисуется полоска опыта. Человеку у поля пароля не нужно ничего из
   * этого. Здесь же — самое место: этап «база» ровно за это и отвечает.
   */
  const [{ собратьБазу }, { открытьБазу }] = await Promise.all([
    import('./net/lobby-data.js'),
    import('./ui/lobby.js'),
  ]);

  const данные = await собратьБазу();
  loader.шаг('база', 1);
  loader.убрать();

  const выбор = await открытьБазу(данные, кто);

  /**
   * ⚠️ АДМИН ВХОДИТ БЕЗ ВОДОЁМА, И ЭТО НЕ ПОБЛАЖКА, А ЕДИНСТВЕННЫЙ ВЫХОД ИЗ
   * ЗАМКНУТОГО КРУГА.
   *
   * Водоёмы заводит админ, обводя берег по настоящей карте. Обводка живёт на
   * панели создателя, то есть ВНУТРИ игры. Пока водоёмов нет, игра не
   * запускается — и разметить первый становится неоткуда: чтобы войти, нужен
   * водоём, а чтобы его сделать, нужно войти.
   *
   * Кнопка «обвести новый водоём» на базе этот круг и разрывает: админ
   * получает пустой мир и свою панель — ровно столько, сколько нужно, чтобы
   * обвести карту и сохранить.
   */
  if (выбор.создать) {
    await поднятьИгру(null);
    return;
  }

  // Журнал закрыли, ничего не выбрав, — открываем снова. Движок не тронут.
  if (выбор.водоём === null) {
    void база(кто);
    return;
  }

  session.worldName = выбор.водоём.name;
  // Список видов нужен запасному источнику поклёвок — тому, что ведёт клёв,
  // когда сервер не поднят. Иначе без сервера клевало бы всё подряд.
  session.allowedFish = выбор.водоём.рыба;
  await поднятьИгру(выбор.водоём.file);
}

/**
 * ⚠️ СОХРАНЁННЫЙ ВХОД НЕ ПОКАЗЫВАЕТ ФОРМУ ВОВСЕ. Есть ключ — человек сразу
 * видит фон и загрузчик, а за ними базу. Форма появится только если ключа нет
 * или он протух: тогда лоадер снимается и слово за человеком.
 */
async function начать(): Promise<void> {
  const ключ = localStorage.getItem('if-auth-token');
  if (ключ !== null && ключ !== '') {
    форма(false);
    loader.показать('база');
    const вошёл = await loginByStoredToken();
    if (вошёл !== null) {
      if (вошёл.role === 'admin') loader.setAdmin(true);
      await база(вошёл);
      return;
    }
    // Ключ негоден и уже стёрт — снимаем лоадер и отдаём слово форме.
    loader.убрать();
  }

  форма(true);
  bindLoginForm((кто) => {
    if (кто.role === 'admin') loader.setAdmin(true);
    void база(кто);
  });
}

void начать();
